import {
  Box,
  Button,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Dialog,
  useDisclosure,
  Badge,
  Flex,
  useToast
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from '../api.js';
import ReleaseModal from '../components/ReleaseModal.jsx';
import PaymentModal from '../components/PaymentModal.jsx';
import PhotoChoiceModal from '../components/PhotoChoiceModal.jsx';
import { useAuth } from '../context/AuthContext';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Transactions = () => {
  const { user } = useAuth();
  const canDelete = user?.role === 'admin' || user?.permissions?.articles?.delete;
  const canEditArticle = user?.role === 'admin' || user?.permissions?.articles?.edit;
  const [filters, setFilters] = useState({
    search: '', // Will now be just Name/Client Search
    amountSearch: '', // New separate filter for Amount
    status: '',
    startDate: '',
    endDate: ''
  });
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null); // For Release
  const [viewArticle, setViewArticle] = useState(null); // For View Details
  const [paymentArticle, setPaymentArticle] = useState(null); // For Payment
  const [editingPayment, setEditingPayment] = useState(null); // For payment edit in view modal
  const [topupArticle, setTopupArticle] = useState(null);
  const [topupForm, setTopupForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    interestRate: ''
  });
  const [savingTopup, setSavingTopup] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [loanEditForm, setLoanEditForm] = useState({
    amount: '',
    date: '',
    interestRate: ''
  });
  const [savingLoanEdit, setSavingLoanEdit] = useState(false);
  
  // --- Edit Mode States ---
  const [editingArticle, setEditingArticle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Photo refs for edit mode
  const photoUploadRef = useRef(null);
  const photoCameraRef = useRef(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedFile, setCapturedFile] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const fetchTransactions = async (params = filters) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    try {
      const result = await apiGet(`/transactions?${query.toString()}`);
      const nextArticles = result.articles || [];
      setArticles(nextArticles);
      if (viewArticle?._id) {
        const updatedView = nextArticles.find(a => a._id === viewArticle._id);
        if (updatedView) setViewArticle(updatedView);
      }
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const clearFilters = () => {
    const cleared = { search: '', amountSearch: '', status: '', startDate: '', endDate: '' };
    setFilters(cleared);
    fetchTransactions(cleared);
  };

  const handleRelease = async ({ releaseDate, file }) => {
    const formData = new FormData();
    formData.append('releaseDate', releaseDate);
    if (file) formData.append('releasePhotoUpload', file);
    await apiUpload(`/articles/${selectedArticle._id}/release`, formData);
    await fetchTransactions();
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete transaction "${article.nameOrNo || 'this item'}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/articles/${article._id}`);
      if (viewArticle?._id === article._id) closeViewModal();
      await fetchTransactions();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const handleDeleteLoan = async (loan) => {
    if (!viewArticle?._id || !loan?._id) return;
    const label = loan.description || 'this principal entry';
    if (!window.confirm(`Delete ${label}? Top-up/principal amount will be removed.`)) return;
    try {
      const res = await apiDelete(`/articles/${viewArticle._id}/loan/${loan._id}`);
      const updated = res?.article;
      if (updated) setViewArticle(updated);
      await fetchTransactions();
    } catch (err) {
      alert(err.message || 'Failed to delete loan entry');
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!viewArticle?._id || !payment?._id) return;
    const msg =
      payment.type === 'interest'
        ? 'Delete this interest payment? Reports will update pending interest.'
        : 'Delete this principal repayment? Total principal balance will increase.';
    if (!window.confirm(msg)) return;
    try {
      const res = await apiDelete(`/articles/${viewArticle._id}/payment/${payment._id}`);
      const updated = res?.article;
      if (updated) {
        setViewArticle(updated);
      }
      await fetchTransactions();
    } catch (err) {
      alert(err.message || 'Failed to delete payment');
    }
  };

  const openViewModal = (article) => {
    setViewArticle(article);
  };

  const closeViewModal = () => {
    setViewArticle(null);
    setEditingPayment(null);
  };

  // --- Edit Handlers ---
  const openEditModal = (article) => {
    setEditingArticle(article);
    setEditForm({
      nameOrNo: article.nameOrNo || '',
      date: article.date ? new Date(article.date).toISOString().split('T')[0] : '',
      weight: article.weight || '',
      amount: article.amount || '', 
      interestRate: article.interestRate || '',
      address: article.address || '',
      description: article.description || '',
      identityProofType: article.identityProof?.type || '',
      identityProofNumber: article.identityProof?.number || ''
    });
    setCapturedFile(null);
    setIsEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editingArticle) return;
    setSavingEdit(true);
    try {
        const formData = new FormData();
        Object.entries(editForm).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        if (capturedFile) {
            formData.append('photo', capturedFile);
        } else if (photoUploadRef.current?.files?.[0]) {
            formData.append('photo', photoUploadRef.current.files[0]);
        }

        await apiUpload(`/articles/${editingArticle._id}`, formData, 'PUT');

        alert('Article updated successfully');
        setIsEditOpen(false);
        setEditingArticle(null);
        fetchTransactions();
    } catch (err) {
        alert(err.message);
    } finally {
        setSavingEdit(false);
    }
  };

  const openTopupModal = (article) => {
    setTopupArticle(article);
    setTopupForm({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      interestRate: article?.interestRate || ''
    });
  };

  const handleTopupChange = (e) => {
    const { name, value } = e.target;
    setTopupForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTopupSubmit = async () => {
    if (!topupArticle) return;
    if (!topupForm.amount || Number(topupForm.amount) <= 0) {
      alert('Please enter valid top-up amount');
      return;
    }

    setSavingTopup(true);
    try {
      await apiPost('/articles', {
        existingArticleId: topupArticle._id,
        amount: Number(topupForm.amount),
        date: topupForm.date,
        interestRate: topupForm.interestRate ? Number(topupForm.interestRate) : undefined
      });
      alert('Top-up added successfully');
      setTopupArticle(null);
      fetchTransactions();
    } catch (err) {
      alert(err.message || 'Failed to add top-up');
    } finally {
      setSavingTopup(false);
    }
  };

  const openLoanEditModal = (loan) => {
    setEditingLoan(loan);
    setLoanEditForm({
      amount: loan?.amount || '',
      date: loan?.date ? new Date(loan.date).toISOString().split('T')[0] : '',
      interestRate: loan?.interestRate || ''
    });
  };

  const handleLoanEditChange = (e) => {
    const { name, value } = e.target;
    setLoanEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLoanEditSubmit = async () => {
    if (!viewArticle?._id || !editingLoan?._id) return;
    if (!loanEditForm.amount || Number(loanEditForm.amount) <= 0) {
      alert('Please enter valid loan amount');
      return;
    }

    setSavingLoanEdit(true);
    try {
      await apiPut(`/articles/${viewArticle._id}/loan/${editingLoan._id}`, {
        amount: Number(loanEditForm.amount),
        date: loanEditForm.date,
        interestRate: loanEditForm.interestRate ? Number(loanEditForm.interestRate) : null,
        description: editingLoan.description || 'Top-up'
      });
      alert('Top-up updated successfully');
      setEditingLoan(null);
      fetchTransactions();
    } catch (err) {
      alert(err.message || 'Failed to update top-up');
    } finally {
      setSavingLoanEdit(false);
    }
  };

  // Camera Handlers for Edit
  const handlePhotoUpload = () => {
    if (photoCameraRef.current) photoCameraRef.current.value = '';
    setPhotoModalOpen(false);
    setCapturedFile(null);
    photoUploadRef.current?.click();
  };

  const handlePhotoCamera = () => {
    setPhotoModalOpen(false);
    setCapturedFile(null);
    setCameraOpen(true);
  };

  const handlePhotoChange = (input) => {
     // File selected via input
  };

  // Camera Stream Logic (Simplified for brevity, similar to Articles.jsx)
  useEffect(() => {
    const startCamera = async () => {
      if (!cameraOpen) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        alert('Camera error');
        setCameraOpen(false);
      }
    };
    startCamera();
    return () => {
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [cameraOpen]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
        const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
        setCapturedFile(file);
        setCameraOpen(false);
    }, 'image/jpeg');
  };

  return (
    <Stack spacing={{ base: 4, md: 6 }}>
      <Heading size={{ base: 'md', md: 'lg' }}>Transactions</Heading>
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
          <form onSubmit={applyFilters}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>Search Name/No</Text>
                <Input name="search" value={filters.search} onChange={handleFilterChange} placeholder="Client Name or Item No" />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Search Amount</Text>
                <Input type="number" name="amountSearch" value={filters.amountSearch} onChange={handleFilterChange} placeholder="Principal Amount" />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Status</Text>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="released">Released</option>
                </select>
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Start Date</Text>
                <Input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>End Date</Text>
                <Input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
              </Box>
              <Box alignSelf={{ base: 'stretch', md: 'flex-end' }}>
                <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
                  <Button type="submit" colorScheme="blue" w={{ base: 'full', sm: 'auto' }}>Filter</Button>
                  <Button variant="outline" onClick={clearFilters} w={{ base: 'full', sm: 'auto' }}>Clear</Button>
                </Stack>
              </Box>
            </SimpleGrid>
          </form>
      </Box>
      {error && <Text color="red.500">{error}</Text>}
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <Box overflowX="auto">
          <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Client</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Name/No</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Vadla Name</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Vadla No</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Release Date</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Total Principal</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Interest</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length ? articles.map(article => (
                <tr key={article._id}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.client?.name || 'N/A'}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.nameOrNo || 'N/A'}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                    {article.vadla?.vadlaName || '-'}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                    {article.vadla?.vadlaNo || '-'}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.date)}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.releaseDate)}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', fontWeight: 'bold' }}>
                    ₹{article.totalPrincipal || article.amount || 0}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                    {article.interestAmount ? `₹${article.interestAmount.toFixed(2)}` : 'N/A'}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.released ? 'Released' : 'Active'}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                    <Button size="xs" colorScheme="blue" variant="ghost" mr={2} onClick={() => openViewModal(article)}>
                      View
                    </Button>
                    {!article.released && user?.permissions?.articles?.edit && (
                        <Button size="xs" colorScheme="gray" variant="outline" mr={2} onClick={() => openEditModal(article)}>
                            Edit
                        </Button>
                    )}
                    {!article.released && user?.permissions?.articles?.create && (
                      <Button size="xs" colorScheme="teal" variant="outline" mr={2} onClick={() => openTopupModal(article)}>
                        Top-Up
                      </Button>
                    )}
                    {!article.released && (
                      <Button size="xs" colorScheme="purple" variant="outline" mr={2} onClick={() => setPaymentArticle(article)}>
                        Pay
                      </Button>
                    )}
                    {!article.released && user?.permissions?.articles?.edit && (
                      <Button size="xs" colorScheme="green" mr={2} onClick={() => setSelectedArticle(article)}>
                        Release
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        aria-label="Delete transaction"
                        onClick={() => handleDelete(article)}
                        px={2}
                        minW="auto"
                      >
                        🗑
                      </Button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td style={{ padding: '8px' }} colSpan={10}>No transactions found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      {/* VIEW DETAILS MODAL */}
      <Dialog.Root 
        open={!!viewArticle} 
        onOpenChange={(e) => {
          if (!e.open) closeViewModal();
        }}
        size="xl"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="85vh" overflowY="auto">
            <Dialog.Header>
              <Dialog.Title>Article Details: {viewArticle?.nameOrNo}</Dialog.Title>
              <Dialog.CloseTrigger onClick={closeViewModal} />
            </Dialog.Header>
            <Dialog.Body>
              {viewArticle && (
                <Stack spacing={4}>
                   <SimpleGrid columns={2} spacing={4}>
                      <Box><Text fontWeight="bold">Client:</Text> {viewArticle.client?.name}</Box>
                      <Box><Text fontWeight="bold">Status:</Text> {viewArticle.released ? 'Released' : 'Active'}</Box>
                      <Box><Text fontWeight="bold">Weight:</Text> {viewArticle.weight}g</Box>
                      <Box><Text fontWeight="bold">Total Principal:</Text> ₹{viewArticle.totalPrincipal}</Box>
                      
                      <Box><Text fontWeight="bold">Address:</Text> {viewArticle.address || 'N/A'}</Box>
                      <Box><Text fontWeight="bold">Description:</Text> {viewArticle.description || 'N/A'}</Box>
                      <Box>
                          <Text fontWeight="bold">ID Proof:</Text> 
                          {viewArticle.identityProof?.type ? `${viewArticle.identityProof.type} - ${viewArticle.identityProof.number}` : 'N/A'}
                      </Box>
                   </SimpleGrid>

                   <Text fontWeight="bold" fontSize="lg" mt={4}>Loan History</Text>
                   <Box overflowX="auto">
                     <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                       <thead>
                         <tr>
                           <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                           <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                           <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                           <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Int. Rate</th>
                           <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Action</th>
                         </tr>
                       </thead>
                       <tbody>
                         {viewArticle.loans && viewArticle.loans.length > 0 ? (
                           viewArticle.loans.map((loan, idx) => (
                             <tr key={`loan-${idx}`}>
                               <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(loan.date)}</td>
                               <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{loan.description || (idx === 0 ? 'Initial' : 'Top-up')}</td>
                               <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{loan.amount}</td>
                               <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{loan.interestRate || viewArticle.interestRate}%</td>
                               <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                 {!viewArticle.released && loan._id && (
                                   <>
                                     {canEditArticle ? (
                                       <Button size="xs" variant="outline" colorScheme="blue" mr={1} onClick={() => openLoanEditModal(loan)}>
                                         Edit
                                       </Button>
                                     ) : null}
                                     {(viewArticle.loans.length > 1 ? canEditArticle : canDelete) ? (
                                       <Button
                                         size="xs"
                                         variant="outline"
                                         colorScheme="red"
                                         onClick={() =>
                                           viewArticle.loans.length > 1
                                             ? handleDeleteLoan(loan)
                                             : handleDelete(viewArticle)
                                         }
                                       >
                                         Delete
                                       </Button>
                                     ) : null}
                                   </>
                                 )}
                               </td>
                             </tr>
                           ))
                         ) : (
                           // Fallback for non-migrated data view
                           <tr>
                             <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(viewArticle.date)}</td>
                             <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>Initial (Legacy)</td>
                             <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{viewArticle.amount}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{viewArticle.interestRate}%</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>-</td>
                           </tr>
                         )}
                       </tbody>
                     </table>
                   </Box>

                   {viewArticle.payments && viewArticle.payments.length > 0 && (
                     <>
                       <Text fontWeight="bold" fontSize="lg" mt={4}>Payment History</Text>
                       <Box overflowX="auto">
                         <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                           <thead>
                             <tr>
                               <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                               <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                               <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                               <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Details</th>
                               <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Action</th>
                             </tr>
                           </thead>
                           <tbody>
                             {viewArticle.payments.map((payment, idx) => (
                               <tr key={payment._id || `payment-${idx}`}>
                                 <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(payment.date)}</td>
                                 <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                                   <Badge colorScheme={payment.type === 'interest' ? 'green' : 'blue'}>
                                     {payment.type.toUpperCase()}
                                   </Badge>
                                 </td>
                                 <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{payment.amount}</td>
                                 <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                                   {payment.description}
                                   {payment.type === 'interest' && payment.months ? ` (${payment.months} mo)` : ''}
                                 </td>
                                 <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                   {!viewArticle.released && payment._id && canEditArticle && (
                                     <>
                                       <Button
                                         size="xs"
                                         variant="outline"
                                         colorScheme="blue"
                                         mr={1}
                                         onClick={() => {
                                           setPaymentArticle(viewArticle);
                                           setEditingPayment(payment);
                                         }}
                                       >
                                         Edit
                                       </Button>
                                       <Button
                                         size="xs"
                                         variant="outline"
                                         colorScheme="red"
                                         onClick={() => handleDeletePayment(payment)}
                                       >
                                         Delete
                                       </Button>
                                     </>
                                   )}
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </Box>
                     </>
                   )}

                   <Box mt={4} pt={2} borderTop="1px solid #e2e8f0">
                      <Flex justify="space-between" align="center">
                        <Text fontWeight="bold">Interest Paid Upto:</Text>
                        <Text>{formatDate(viewArticle.interestPaidUpto) || 'N/A'}</Text>
                      </Flex>
                      <Flex justify="space-between" align="center" mt={2}>
                        <Text fontWeight="bold" fontSize="lg">Total Principal Balance:</Text>
                        <Text fontWeight="bold" fontSize="lg" color="green.600">₹{viewArticle.totalPrincipal}</Text>
                      </Flex>
                   </Box>
                </Stack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorScheme="blue" mr={3} onClick={closeViewModal}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* EDIT TOP-UP MODAL */}
      <Dialog.Root
        open={!!editingLoan}
        onOpenChange={(e) => {
          if (!e.open) setEditingLoan(null);
        }}
        size="md"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Edit Top-Up</Dialog.Title>
              <Dialog.CloseTrigger onClick={() => setEditingLoan(null)} />
            </Dialog.Header>
            <Dialog.Body>
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>Amount</Text>
                  <Input type="number" name="amount" value={loanEditForm.amount} onChange={handleLoanEditChange} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Date</Text>
                  <Input type="date" name="date" value={loanEditForm.date} onChange={handleLoanEditChange} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Interest Rate</Text>
                  <Input type="number" name="interestRate" value={loanEditForm.interestRate} onChange={handleLoanEditChange} />
                </Box>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setEditingLoan(null)}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleLoanEditSubmit} isLoading={savingLoanEdit}>Update</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
      
      {/* EDIT MODAL */}
      <Dialog.Root 
        open={isEditOpen} 
        onOpenChange={(e) => {
          if (!e.open) setIsEditOpen(false);
        }}
        size="lg"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
            <Dialog.Content>
                <Dialog.Header>
                    <Dialog.Title>Edit Article</Dialog.Title>
                    <Dialog.CloseTrigger onClick={() => setIsEditOpen(false)} />
                </Dialog.Header>
                <Dialog.Body>
                    <Stack spacing={4}>
                        <Box>
                            <Text fontSize="sm" mb={1}>Name/No</Text>
                            <Input name="nameOrNo" value={editForm.nameOrNo} onChange={handleEditChange} />
                        </Box>
                        <SimpleGrid columns={2} spacing={4}>
                            <Box>
                                <Text fontSize="sm" mb={1}>Date</Text>
                                <Input type="date" name="date" value={editForm.date} onChange={handleEditChange} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>Weight</Text>
                                <Input type="number" name="weight" value={editForm.weight} onChange={handleEditChange} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>Principal Amount</Text>
                                <Input type="number" name="amount" value={editForm.amount} onChange={handleEditChange} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>Interest Rate</Text>
                                <Input type="number" name="interestRate" value={editForm.interestRate} onChange={handleEditChange} />
                            </Box>
                        </SimpleGrid>
                        <Box>
                             <Text fontSize="sm" mb={1}>Address</Text>
                             <Input name="address" value={editForm.address} onChange={handleEditChange} />
                        </Box>
                         <Box>
                             <Text fontSize="sm" mb={1}>Description</Text>
                             <Input name="description" value={editForm.description} onChange={handleEditChange} />
                        </Box>
                        <Box>
                            <Text fontSize="sm" mb={1}>Update Photo (Optional)</Text>
                            <Button size="sm" onClick={() => setPhotoModalOpen(true)}>Choose Photo</Button>
                            {capturedFile && <Text fontSize="xs" color="green.500">New photo selected</Text>}
                            <Input ref={photoUploadRef} type="file" hidden onChange={handlePhotoChange} />
                        </Box>
                    </Stack>
                </Dialog.Body>
                <Dialog.Footer>
                    <Button variant="ghost" mr={3} onClick={() => setIsEditOpen(false)}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleEditSubmit} isLoading={savingEdit}>Save Changes</Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* TOP-UP MODAL */}
      <Dialog.Root
        open={!!topupArticle}
        onOpenChange={(e) => {
          if (!e.open) setTopupArticle(null);
        }}
        size="md"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Add Top-Up: {topupArticle?.nameOrNo || 'Article'}</Dialog.Title>
              <Dialog.CloseTrigger onClick={() => setTopupArticle(null)} />
            </Dialog.Header>
            <Dialog.Body>
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>Top-Up Amount</Text>
                  <Input type="number" name="amount" value={topupForm.amount} onChange={handleTopupChange} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Top-Up Date</Text>
                  <Input type="date" name="date" value={topupForm.date} onChange={handleTopupChange} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Interest Rate</Text>
                  <Input type="number" name="interestRate" value={topupForm.interestRate} onChange={handleTopupChange} />
                </Box>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setTopupArticle(null)}>Cancel</Button>
              <Button colorScheme="teal" onClick={handleTopupSubmit} isLoading={savingTopup}>Save Top-Up</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <PhotoChoiceModal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        onUpload={handlePhotoUpload}
        onCamera={handlePhotoCamera}
      />
      
      {/* Camera UI Overlay */}
      {cameraOpen && (
        <Box position="fixed" inset="0" zIndex="1500">
          <Box position="absolute" inset="0" bg="blackAlpha.700" onClick={() => setCameraOpen(false)} />
          <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" bg="white" w={{ base: '92%', sm: '420px' }} borderRadius="md" p={4} boxShadow="lg">
            <Text fontSize="lg" fontWeight="semibold" mb={3}>Camera</Text>
            <Box as="video" ref={videoRef} autoPlay playsInline muted w="full" borderRadius="md" bg="black" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Stack direction="row" spacing={3} mt={4} justify="flex-end">
              <Button variant="ghost" onClick={() => setCameraOpen(false)}>Cancel</Button>
              <Button colorScheme="blue" onClick={capturePhoto}>Take Photo</Button>
            </Stack>
          </Box>
        </Box>
      )}

      {selectedArticle && (
        <ReleaseModal
          isOpen={Boolean(selectedArticle)}
          onClose={() => setSelectedArticle(null)}
          article={selectedArticle}
          onSubmit={handleRelease}
        />
      )}

      {(paymentArticle || editingPayment) && (
        <PaymentModal
          isOpen={Boolean(paymentArticle || editingPayment)}
          onClose={() => {
            setPaymentArticle(null);
            setEditingPayment(null);
          }}
          article={paymentArticle || (editingPayment ? viewArticle : null)}
          paymentToEdit={editingPayment}
          onPaymentSuccess={async () => {
            await fetchTransactions();
            setPaymentArticle(null);
            setEditingPayment(null);
          }}
        />
      )}
    </Stack>
  );
};

export default Transactions;
